import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { Observable } from "rxjs";
import { UserNotify } from "src/app/support/notify-decorator";
import { environment } from "src/environments/environment";
import { Cart, CartItem } from "../models/cart";
import { sntTranslate } from "src/app/support/translate";
import { map } from "rxjs/operators";
import { first } from "lodash-es";

@Injectable()
export class CartBackend {
  constructor(private http: HttpClient) {}

  load(cartId: string): Observable<Cart> {
    return this.http.get<Cart>(
      `${environment.snoots.api.billing}/carts/${cartId}`,
      {
        headers: {
          "snt-no-cache": "true"
        }
      }
    );
  }

  find(bookId: string): Observable<Cart[]> {
    return this.http.get<Cart[]>(
      `${environment.snoots.api.billing}/books/${bookId}/carts`
    );
  }

  findSingle(bookId: string): Observable<Cart | undefined> {
    return this.http
      .get<Cart[]>(`${environment.snoots.api.billing}/books/${bookId}/carts`)
      .pipe(map((carts) => first(carts)));
  }

  @UserNotify({
    successMessage: marker("Cassa creata"),
    errorMessage: marker("Impossibile creare il conto")
  })
  create(
    bookId: string,
    adjustment?: number,
    discount?: number
  ): Observable<Cart> {
    return this.http.post<Cart>(
      `${environment.snoots.api.billing}/books/${bookId}/carts`,
      { adjustment, discount }
    );
  }

  @UserNotify({
    successMessage: marker("Cassa aggiornata"),
    errorMessage: marker("Impossibile aggiornare la cassa")
  })
  update(id: string, request: Partial<Cart>): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.billing}/carts/${id}`,
      request
    );
  }

  @UserNotify({
    successMessage: marker("Articoli rimossi dalla cassa"),
    errorMessage: marker("Impossibile rimuovere gli articoli dalla cassa")
  })
  clear(id: string): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.billing}/carts/${id}`,
      {
        items: []
      }
    );
  }

  @UserNotify({
    successMessage: (cart: Cart, cartItem: Partial<CartItem>) =>
      cartItem.name
        ? `${cartItem.name} ${sntTranslate("aggiunto alla cassa")}`
        : sntTranslate(marker("Aggiunto alla cassa")),
    errorMessage: marker("Impossibile aggiungere alla cassa")
  })
  add(cartId: string, request: Partial<CartItem>): Observable<Cart> {
    return this.http.post<Cart>(
      `${environment.snoots.api.billing}/carts/${cartId}/items`,
      request
    );
  }

  @UserNotify({
    successMessage: marker("Item aggiornato"),
    errorMessage: marker("Impossibile aggiornare l'item selezionato")
  })
  updateItem(
    cartId: string,
    cartItemId: string,
    request: Partial<CartItem>
  ): Observable<Cart> {
    return this.http.patch<Cart>(
      `${environment.snoots.api.billing}/carts/${cartId}/items/${cartItemId}`,
      request
    );
  }

  @UserNotify({
    successMessage: (cart: Cart, cartItem: CartItem) =>
      `${cartItem.name} ${sntTranslate(marker("rimosso dalla cassa"))}`,
    errorMessage: marker("Impossibile rimuovere dalla cassa")
  })
  remove(id: string, cartItem: CartItem): Observable<Cart> {
    return this.http.delete<Cart>(
      `${environment.snoots.api.billing}/carts/${id}/items/${cartItem.id}`
    );
  }
}
